<template>
  <div class="auth-container">
    <div class="auth-card">
      <h2 class="auth-title">パスワードリセット</h2>
      
      <form @submit.prevent="handleResetPassword" class="auth-form">
        <div class="form-group">
          <label for="newPassword">新しいパスワード</label>
          <input
            id="newPassword"
            v-model="form.newPassword"
            type="password"
            required
            placeholder="8文字以上で入力"
            minlength="8"
            class="form-input"
          />
        </div>
        
        <div class="form-group">
          <label for="confirmPassword">パスワード（確認）</label>
          <input
            id="confirmPassword"
            v-model="form.confirmPassword"
            type="password"
            required
            placeholder="パスワードを再入力"
            class="form-input"
          />
        </div>
        
        <button type="submit" :disabled="isLoading" class="btn btn-primary">
          {{ isLoading ? '更新中...' : 'パスワードを更新' }}
        </button>
      </form>
      
      <!-- エラーメッセージ -->
      <div v-if="error" class="error-message">
        {{ error }}
      </div>
      
      <!-- 成功メッセージ -->
      <div v-if="successMessage" class="success-message">
        {{ successMessage }}
        <div class="success-links">
          <router-link to="/login" class="btn-link">ログインページへ</router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { supabase } from '../../supabaseClient'

const router = useRouter()
const route = useRoute()

// 状態管理
const isLoading = ref(false)
const error = ref('')
const successMessage = ref('')

// フォームデータ
const form = reactive({
  newPassword: '',
  confirmPassword: ''
})

// パスワードリセット処理
const handleResetPassword = async () => {
  try {
    isLoading.value = true
    error.value = ''
    
    // パスワード確認
    if (form.newPassword !== form.confirmPassword) {
      error.value = 'パスワードが一致しません'
      return
    }
    
    // パスワード長チェック
    if (form.newPassword.length < 8) {
      error.value = 'パスワードは8文字以上で入力してください'
      return
    }
    
    // パスワード更新
    const { error: updateError } = await supabase.auth.updateUser({
      password: form.newPassword
    })
    
    if (updateError) throw updateError
    
    successMessage.value = 'パスワードが正常に更新されました。まもなくトップページに移動します。'
    
    // 3秒後にトップページへリダイレクト
    setTimeout(() => {
      router.push('/')
    }, 3000)
    
  } catch (err) {
    error.value = err.message || 'パスワードの更新に失敗しました'
  } finally {
    isLoading.value = false
  }
}

// ページロード時の処理
onMounted(() => {
  // URLパラメータからハッシュを取得して検証
  const hash = route.hash
  if (!hash) {
    error.value = '無効なリセットリンクです'
    return
  }
  
  // ハッシュからアクセストークンを抽出
  const accessToken = hash.replace('#access_token=', '').split('&')[0]
  if (!accessToken) {
    error.value = 'アクセストークンが見つかりません'
    return
  }
  
  // セッションを設定
  supabase.auth.setSession({
    access_token: accessToken,
    refresh_token: ''
  })
})
</script>

<style scoped>
.auth-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 1rem;
}

.auth-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  padding: 2rem;
  width: 100%;
  max-width: 400px;
}

.auth-title {
  text-align: center;
  color: #333;
  margin-bottom: 2rem;
  font-size: 1.5rem;
  font-weight: 600;
}

.auth-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group label {
  font-weight: 500;
  color: #555;
  font-size: 0.9rem;
}

.form-input {
  padding: 0.75rem;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.2s;
}

.form-input:focus {
  outline: none;
  border-color: #667eea;
}

.btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background: #667eea;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #5a6fd8;
  transform: translateY(-1px);
}

.error-message {
  background: #fee;
  color: #c53030;
  padding: 0.75rem;
  border-radius: 8px;
  margin-top: 1rem;
  font-size: 0.9rem;
}

.success-message {
  background: #f0fff4;
  color: #22543d;
  padding: 0.75rem;
  border-radius: 8px;
  margin-top: 1rem;
  font-size: 0.9rem;
}

.success-links {
  margin-top: 1rem;
  text-align: center;
}

.btn-link {
  color: #667eea;
  text-decoration: underline;
  font-size: 0.9rem;
}

.btn-link:hover {
  color: #5a6fd8;
}

@media (max-width: 480px) {
  .auth-card {
    padding: 1.5rem;
    margin: 1rem;
  }
}
</style>
